import React from 'react';
import { usePermissions, ROLES, PERMISSIONS } from '../hooks/usePermissions';
import PermissionGate from '../components/PermissionGate';
import { Shield, User, Key, CheckCircle, XCircle } from 'lucide-react';

const PermissionsTest: React.FC = () => {
  const {
    currentRole,
    userName,
    userId,
    isAdmin,
    isChef,
    isPersonalCocina,
    isCliente,
    hasPermission,
    hasRoleOrHigher,
  } = usePermissions();

  const testPermissions = [
    PERMISSIONS.USUARIOS_CREATE,
    PERMISSIONS.INGREDIENTES_READ,
    PERMISSIONS.RECETAS_CREATE,
    PERMISSIONS.MINUTAS_UPDATE,
    PERMISSIONS.CLIENTES_READ,
    PERMISSIONS.REPORTES_ADVANCED,
  ];

  const testRoles = [
    ROLES.ADMINISTRADOR,
    ROLES.CHEF,
    ROLES.PERSONAL_COCINA,
    ROLES.CLIENTE,
  ];

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
          <Shield className="h-8 w-8 mr-3 text-blue-600" />
          Sistema de Permisos - Test
        </h1>
        <p className="text-gray-600">
          Verificación del sistema de roles y permisos implementado
        </p>
      </div>

      {/* Información del Usuario */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <User className="h-5 w-5 mr-2" />
          Información del Usuario
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500">Nombre</p>
            <p className="text-lg">{userName || 'No disponible'}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">ID</p>
            <p className="text-lg">{userId || 'No disponible'}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Rol Actual</p>
            <p className="text-lg font-semibold text-blue-600">
              {currentRole || 'No disponible'}
            </p>
          </div>
        </div>
      </div>

      {/* Verificación de Roles */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <Key className="h-5 w-5 mr-2" />
          Verificación de Roles
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Roles específicos */}
          <div>
            <h3 className="font-medium mb-3">Roles Específicos</h3>
            <div className="space-y-2">
              <div className="flex items-center">
                {isAdmin ? (
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-500 mr-2" />
                )}
                <span>Es Administrador</span>
              </div>
              <div className="flex items-center">
                {isChef ? (
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-500 mr-2" />
                )}
                <span>Es Chef</span>
              </div>
              <div className="flex items-center">
                {isPersonalCocina ? (
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-500 mr-2" />
                )}
                <span>Es Personal de Cocina</span>
              </div>
              <div className="flex items-center">
                {isCliente ? (
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-500 mr-2" />
                )}
                <span>Es Cliente</span>
              </div>
            </div>
          </div>

          {/* Test de jerarquía */}
          <div>
            <h3 className="font-medium mb-3">Jerarquía de Roles</h3>
            <div className="space-y-2">
              {testRoles.map((role) => (
                <div key={role} className="flex items-center">
                  {hasRoleOrHigher(role) ? (
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500 mr-2" />
                  )}
                  <span>Nivel {role} o superior</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Verificación de Permisos */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">
          Verificación de Permisos Específicos
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {testPermissions.map((permission) => (
            <div key={permission} className="flex items-center">
              {hasPermission(permission) ? (
                <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
              ) : (
                <XCircle className="h-4 w-4 text-red-500 mr-2" />
              )}
              <span className="text-sm">{permission}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Demostración de PermissionGate */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">
          Demostración de PermissionGate
        </h2>

        <div className="space-y-4">
          <PermissionGate permission={PERMISSIONS.USUARIOS_CREATE}>
            <div className="p-4 bg-green-50 border border-green-200 rounded-md">
              ✅ Puedes ver esto porque tienes permiso para crear usuarios
            </div>
          </PermissionGate>

          <PermissionGate
            permission={PERMISSIONS.USUARIOS_CREATE}
            showFallback={true}
            fallback={
              <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                ❌ No tienes permiso para crear usuarios
              </div>
            }
          >
            <div className="p-4 bg-green-50 border border-green-200 rounded-md">
              ✅ Puedes crear usuarios
            </div>
          </PermissionGate>

          <PermissionGate role={[ROLES.ADMINISTRADOR, ROLES.CHEF]}>
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
              ✅ Eres Administrador o Chef
            </div>
          </PermissionGate>

          <PermissionGate
            role={[ROLES.ADMINISTRADOR, ROLES.CHEF]}
            showFallback={true}
            fallback={
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                ⚠️ No eres Administrador ni Chef
              </div>
            }
          >
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
              ✅ Eres Administrador o Chef
            </div>
          </PermissionGate>
        </div>
      </div>
    </div>
  );
};

export default PermissionsTest;
